import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import appointmentService from '../../../services/appointment.service';
import { confirmAction, showSuccess } from '../../../utils/ecxSwal';
import {
    getAppointmentStatusMeta,
    getPatientInitials,
} from './subparts/appointmentsQuery';
import AppointmentActionsMenu from './AppointmentActionsMenu';

function AppointmentCalendarBlock({
    appt,
    isSelected,
    onCheckChange,
    onStatusUpdated,
    density = 'full',
    isCompact = false,
    hideDoctor = false,
    style,
}) {
    const [updating, setUpdating] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const resolvedDensity = density || (isCompact ? 'compact' : 'full');
    const isMicro = resolvedDensity === 'micro';
    const isDense = resolvedDensity === 'compact' || isMicro;
    const showMeta = resolvedDensity === 'full';
    const showAvatar = !isMicro;
    const showTime = !isMicro;

    const patient = appt.patient || {};
    const doctor = appt.doctor || {};
    const statusMeta = getAppointmentStatusMeta(appt.isConfirmed);
    const initials = getPatientInitials(patient);
    const patientName = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim() || 'Patient';
    const timeLabel = `${appt.startTime} – ${appt.endTime}`;

    const handleConfirmCancel = async (confirmed) => {
        try {
            setUpdating(true);
            await appointmentService.updateAppointmentStatus(appt.clinicId, appt.id, confirmed);
            onStatusUpdated?.();
        } catch (error) {
            console.error(error);
            toast.error('Failed to update appointment status.');
        } finally {
            setUpdating(false);
        }
    };

    const handleSendReminder = async () => {
        setMenuOpen(false);

        const result = await confirmAction({
            title: 'Send reminder?',
            html: `Send a <strong>Second Reminder</strong> to <strong>${patientName}</strong> using their preferred communication channel (email, SMS, or call).`,
            iconClass: 'bi bi-send',
            iconTone: 'primary',
            confirmText: 'Send Reminder',
            onConfirm: async () => {
                setUpdating(true);
                try {
                    return await appointmentService.sendAppointmentReminder(appt.clinicId, appt.id);
                } finally {
                    setUpdating(false);
                }
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        const medium = result.value?.data?.mediumType || result.value?.mediumType;
        const mediumLabel =
            medium === 'EMAIL' ? 'Email' : medium === 'SMS' ? 'SMS' : medium === 'CALL' ? 'Call' : 'Reminder';
        showSuccess({
            title: 'Reminder Queued',
            text: `${mediumLabel} reminder queued for ${patientName}.`,
        });
        onStatusUpdated?.();
    };

    return (
        <div
            className={`appointments-calendar-block appointments-calendar-block--${statusMeta.variant}${
                isSelected ? ' is-selected' : ''
            }${isDense ? ' is-compact' : ''}${isMicro ? ' is-micro' : ''}${
                menuOpen ? ' is-menu-open' : ''
            }`}
            style={style}
            title={isMicro ? `${patientName} · ${timeLabel}` : undefined}
        >
            <div className="appointments-calendar-block__header">
                <div className="appointments-calendar-block__main">
                    <label className="appointments-calendar-block__check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isSelected}
                            onChange={(event) => onCheckChange(appt.id, event.target.checked)}
                            aria-label={`Select appointment for ${patientName}`}
                        />
                    </label>

                    {showAvatar && (
                        <span className="appointments-calendar-block__avatar" aria-hidden="true">
                            {initials || <i className="bi bi-person-fill" />}
                        </span>
                    )}

                    <div className="appointments-calendar-block__title min-width-0">
                        <Link
                            to={`/patient-files/${appt.patientId}`}
                            className="appointments-calendar-block__name"
                            title={patientName}
                        >
                            {patientName}
                        </Link>
                        {showTime && (
                            <div className="appointments-calendar-block__time">{timeLabel}</div>
                        )}
                    </div>
                </div>

                <div className="appointments-actions appointments-calendar-block__actions">
                    {appt.isConfirmed === 0 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success appointments-calendar-block__confirm"
                            onClick={() => handleConfirmCancel(true)}
                            disabled={updating}
                            title="Confirm appointment"
                        >
                            <i className="bi bi-check2-circle" />
                        </button>
                    )}

                    <AppointmentActionsMenu
                        appt={appt}
                        updating={updating}
                        menuOpen={menuOpen}
                        onMenuToggle={setMenuOpen}
                        onConfirmCancel={handleConfirmCancel}
                        onSendReminder={handleSendReminder}
                        usePortal
                    />
                </div>
            </div>

            {showMeta && (
                <div
                    className={`appointments-calendar-block__meta${
                        hideDoctor ? ' appointments-calendar-block__meta--status-only' : ''
                    }`}
                >
                    {!hideDoctor && (
                        <span className="appointments-calendar-block__doctor">
                            Dr. {doctor.firstName} {doctor.lastName}
                        </span>
                    )}
                    <span className={`appointments-status-pill appointments-status-pill--${statusMeta.variant}`}>
                        <i className={`bi ${statusMeta.icon}`} />
                        {statusMeta.label}
                    </span>
                </div>
            )}
        </div>
    );
}

export default AppointmentCalendarBlock;
